import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import {
  QrCode,
  Search,
  Bell,
  Sun,
  Moon,
  User,
  Settings,
  LogOut,
  Menu,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import Sidebar from './Sidebar';

export function Topbar() {
  const { theme, toggleTheme } = useTheme();
  const { appTheme } = useAppTheme();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);

  // Determine which logo to use based on color mode
  const logoUrl = theme === 'dark' && appTheme.admin_logo_dark 
    ? appTheme.admin_logo_dark 
    : appTheme.admin_logo;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex h-full items-center justify-between px-4 lg:px-6">
      {/* Left section: Logo and mobile menu */}
      <div className="flex items-center gap-4">
        {/* Mobile menu trigger */}
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[272px] p-0">
            <Sidebar />
          </SheetContent>
        </Sheet>

        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="LinkHub" 
              className="h-8 w-auto object-contain"
            />
          ) : (
            <>
              <div 
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ backgroundColor: appTheme.primary_color || '#154734' }}
              >
                <QrCode className="h-5 w-5 text-white" />
              </div>
              <span className="hidden font-semibold text-lg sm:inline-block">
                LinkHub
              </span>
            </>
          )}
        </Link>
      </div>

      {/* Center section: Search */}
      <div className="hidden md:flex flex-1 max-w-md mx-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search QR codes... (Cmd+K)"
            className="pl-9 bg-muted/50"
            data-testid="global-search-input"
          />
        </div>
      </div>

      {/* Right section: Actions */}
      <div className="flex items-center gap-2">
        {/* Mobile search */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          data-testid="mobile-search-button"
        >
          <Search className="h-5 w-5" />
          <span className="sr-only">Search</span>
        </Button>

        {/* Theme toggle */}
        <div className="flex items-center gap-2">
          <Sun className="h-4 w-4 text-muted-foreground" />
          <Switch
            checked={theme === 'dark'}
            onCheckedChange={toggleTheme}
            aria-label="Toggle dark mode"
            data-testid="theme-toggle"
          />
          <Moon className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Notifications */}
        <Button variant="ghost" size="icon" data-testid="notifications-button">
          <Bell className="h-5 w-5" />
          <span className="sr-only">Notifications</span>
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-button">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.full_name || 'User'}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email || 'user@company.com'}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings/profile" className="cursor-pointer" data-testid="profile-menu-item">
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer" data-testid="settings-menu-item">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive" data-testid="logout-menu-item">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Pam Golding gates — brand mark, top-right (mirrors the LinkHub logo top-left) */}
        <div className="hidden sm:flex items-center ml-1 pl-3 border-l border-border">
          <img
            src={theme === 'dark' ? '/pgp-gates-white.svg' : '/pgp-gates-green.svg'}
            alt="Pam Golding Properties"
            className="h-7 w-auto object-contain"
            data-testid="brand-gates"
          />
        </div>
      </div>
    </div>
  );
}

export default Topbar;
